import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Cookies from 'js-cookie';
import moment from 'moment-timezone';
import { useTranslation } from 'react-i18next';
import { Avatar, Card, QGlyph, ServerBadge, Icons, PublicHeader, LoadingScreen, ErrorScreen } from './ui';
import { TranslateText } from './TranslateText';

const APP_URL = process.env.REACT_APP_APP_URL || window.location.origin;

interface AnswerPageData {
  question: {
    id: number;
    server: string;
    user_id: string;
    question: string;
    answer: string;
    created_at: string;
    answerd_at: string;
    nsfw: boolean;
    not_anonymous: boolean;
    questioner_user_id: string;
    questioner_server: string;
  };
  display_name: string;
  avatar: string;
  box_name: string | null;
}

const UTCtoJST = (d: string) =>
  moment.utc(d).tz('Asia/Tokyo').format('YYYY/MM/DD HH:mm');

const AnswerDetail: React.FC = () => {
  const { t } = useTranslation();
  const { user_id, question_id } = useParams<{ user_id: string; question_id: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<AnswerPageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const loggedIn = !!Cookies.get('jwtToken');

  useEffect(() => {
    if (!question_id) return;
    fetch(`/api/v1/answer/${question_id}`)
      .then(r => {
        if (!r.ok) throw r.status;
        return r.json();
      })
      .then(setData)
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [question_id]);

  if (loading) return <LoadingScreen />;
  if (notFound || !data) {
    return (
      <ErrorScreen
        title={t('answerDetail.notFoundTitle')}
        message={t('answerDetail.notFoundDesc')}
        onBack={() => navigate(user_id ? `/questionbox/${user_id}` : '/')}
      />
    );
  }

  const { question: q, display_name, avatar } = data;
  const boxUrl = `/questionbox/@${q.user_id}@${q.server}`;
  const shareUrl = `${APP_URL}/questionbox/@${q.user_id}@${q.server}/${q.id}`;

  return (
    <div className="min-h-screen bg-ink-50 dark:bg-ink-950 text-ink-800 dark:text-ink-100">
      <PublicHeader
        loggedIn={loggedIn}
        backTo={boxUrl}
        backLabel={t('answerDetail.backLabel')}
      />

      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10 space-y-6">
        {/* Answerer info */}
        <div className="flex items-center gap-3">
          <Avatar src={avatar} seed={display_name} size={44} />
          <div>
            <div className="font-bold">{display_name}</div>
            <div className="flex items-center gap-2 mt-0.5">
              <a
                href={`https://${q.server}/@${q.user_id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[12px] text-ink-500 font-mono hover:underline"
              >
                @{q.user_id}@{q.server}
              </a>
              <ServerBadge host={q.server} />
            </div>
          </div>
        </div>

        {/* Question */}
        <Card className="!p-6">
          <div className="flex items-start gap-3 mb-1">
            <Avatar
              anonymous={!q.not_anonymous}
              seed={q.questioner_user_id || 'A'}
              size={28}
            />
            <div className="flex-1">
              <span className="text-[12px] text-ink-500 dark:text-ink-400">
                {q.not_anonymous && q.questioner_user_id
                  ? `@${q.questioner_user_id}@${q.questioner_server}`
                  : t('common.anonymous')}
              </span>
              {q.nsfw && (
                <span className="ml-2 text-[10px] font-bold text-rose-500 border border-rose-300 rounded px-1">
                  NSFW
                </span>
              )}
            </div>
            <span className="text-[11px] text-ink-400 shrink-0">{UTCtoJST(q.created_at)}</span>
          </div>
          <p className="text-[16px] leading-relaxed mt-3 font-medium"><TranslateText text={q.question} inline /></p>
        </Card>

        {/* Answer */}
        <Card className="!p-6 ring-2 ring-brand-blue-100 dark:ring-brand-blue-900/40">
          <div className="flex items-center gap-2 mb-4">
            <QGlyph tone="blue" size={28} />
            <span className="text-[13px] font-semibold text-brand-blue-600 dark:text-brand-blue-300">
              {t('answerDetail.answerLabel')}
            </span>
            <span className="ml-auto text-[11px] text-ink-400">{UTCtoJST(q.answerd_at)}</span>
          </div>
          <p className="text-[15px] leading-relaxed whitespace-pre-wrap"><TranslateText text={q.answer} inline /></p>
        </Card>

        {/* Actions */}
        <div className="flex items-center justify-between pt-2">
          <Link
            to={boxUrl}
            className="text-[13px] text-brand-blue-500 hover:underline flex items-center gap-1"
          >
            <Icons.Home width={13} height={13} />
            {t('answerDetail.questionBoxOf', { name: display_name })}
          </Link>
          <button
            onClick={() => navigator.clipboard.writeText(shareUrl).catch(() => {})}
            className="h-8 px-3 rounded-full border border-ink-200 dark:border-ink-700 text-[12px] font-medium flex items-center gap-1.5 bg-white dark:bg-ink-900 hover:bg-ink-100 dark:hover:bg-ink-800 transition text-ink-600 dark:text-ink-300 shadow-sm"
          >
            <Icons.ExternalLink width={13} height={13} />
            {t('common.copyUrl')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AnswerDetail;
